import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Form,
  Modal,
  Descriptions,
  Skeleton,
} from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Button, Text, Title } from 'components';
import { useSelector } from 'react-redux';
import {
  selectCategoriasDeQuejas,
  selectMotivosDeQuejas,
  selectSubMotivosDeQuejas,
  selectTiposDeVisitasDomiciliarias,
} from 'store/catalogos';
import { getPlantillasNoIndexadas } from 'api/tramites';
import { getDependencias } from 'api/configuracion';
import padronToString from 'utils/padronesToString';

const RowSkeleton = () => (
  <Skeleton.Input
    active
    loading
    size="small"
    style={{ borderRadius: 5 }}
    block
  />
);

const ModalValidateInfo = ({
  visible,
  setVisible,
  isAnonimo,
  testigos,
  callback,
}) => {
  const motivos = useSelector(selectMotivosDeQuejas);
  const categorias = useSelector(selectCategoriasDeQuejas);
  const subMotivos = useSelector(selectSubMotivosDeQuejas);
  const tiposDeVisitasDomiciliarias = useSelector(selectTiposDeVisitasDomiciliarias);
  const form = Form.useFormInstance();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const fetchPlantilla = async (id) => {
    const { results } = await getPlantillasNoIndexadas({ id });
    if (!results.length) return '';
    return results[0].nombre;
  };

  const fetchOficinaDeAtencion = async (id) => {
    const { results } = await getDependencias({ id });
    if (!results.length) return '';
    return results[0].descripcion;
  };

  const setValues = async () => {
    setLoading(true);
    setIsConfirmed(false);
    const formValues = form.getFieldsValue();

    let solicitante = {};
    if (isAnonimo) {
      solicitante = {
        correo_de_solicitante: formValues.correo_de_solicitante,
      };
    } else {
      solicitante = {
        padron: formValues.tipo_de_padron,
        correo: formValues?.email || formValues?.correo_electronico,
        nombre: padronToString(formValues, formValues.tipo_de_padron),
        telefono: formValues?.numero_de_celular || formValues?.telefono_celular,
        key: formValues.tipo_de_padron === 1 ? formValues.CURP : formValues.rfc,
      };
    }
    const tramite = formValues?.tramite_de_queja
      ? formValues?.tramite_de_queja
      : await fetchPlantilla(formValues.plantilla_de_tramites);
    const oficina = formValues?.oficina_de_queja
      ? formValues?.oficina_de_queja
      : await fetchOficinaDeAtencion(formValues.oficina_de_atencion);

    const _data = {
      ...formValues,
      submotivo: subMotivos.find((e) => e.id === formValues.submotivo_de_la_queja)?.descripcion,
      motivo: motivos.find((e) => e.id === formValues.motivo_de_la_queja)?.descripcion,
      categoria: categorias.find((e) => e.id === formValues.categoria_de_la_queja)?.descripcion,
      fecha: formValues.fecha_de_los_hechos?.format('DD/MM/YYYY'),
      hora: formValues.hora_de_los_hechos?.format('h:mm a'),
      detalle: formValues.detalle_de_hechos,
      institucion: formValues.institucion_de_queja,
      hay_evidencias: formValues.tiene_pruebas ? 'Si' : 'No',
      hay_testigos: !!testigos.length,
      oficina,
      tramite,
      solicitante,
    };
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      setValues();
    }
  }, [visible]);

  return (
    <Modal
      title={(
        <Title level={4}>
          {isConfirmed ? 'Confirmar' : 'Resumen '}
        </Title>
        )}
      visible={visible}
      centered
      width={isConfirmed ? 600 : 800}
      onCancel={() => setVisible(false)}
      maskClosable={false}
      footer={[
        <Button
          key="back"
          onClick={() => {
            if (isConfirmed) {
              setIsConfirmed(false);
            } else {
              setVisible(false);
            }
          }}
          icon={<ArrowLeftOutlined />}
        >
          Regresar
        </Button>,
        <Button
          key="submit"
          $accent={!loading}
          onClick={() => {
            if (isConfirmed) {
              callback();
            } else {
              setIsConfirmed(true);
            }
          }}
          icon={<CheckCircleOutlined />}
          disabled={loading}
        >
          {isConfirmed ? 'Enviar' : 'Confirmar'}
        </Button>,
      ]}
    >
      {!isConfirmed ? (
        <>
          {isAnonimo ? (
            <Descriptions
              title={(
                <Title level={4} $bold>
                  Queja anonima
                </Title>
              )}
              column={1}
              layout="vertical"
              colon
            >
              <Descriptions.Item label={(
                <Text $accent $bold>
                  Correo electronico al que se enviara la respuesta
                </Text>
              )}
              >
                {loading ? (
                  <RowSkeleton loading={loading} />
                ) : (
                  <Text>
                    {data?.solicitante?.correo_de_solicitante}
                  </Text>
                )}
              </Descriptions.Item>
            </Descriptions>

          ) : (
            <Descriptions
              title={(
                <Title level={4} $bold>
                  Información del solicitante
                </Title>
              )}
              column={1}
              bordered
              size="small"
            >
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    Nombre
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.solicitante?.nombre}
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    Correo electronico
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.solicitante?.correo}
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    Telefono
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.solicitante?.telefono}
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    {data?.solicitante?.padron === 1 ? 'CURP' : 'RFC'}
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.solicitante?.key}
                  </Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          )}
          <Divider />
          <Descriptions
            title={(
              <Title level={4} $bold>
                Información General
              </Title>
            )}
            column={2}
            layout="vertical"
            colon={false}
          >
            {data?.es_tramite ? (
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    ¿Qué se está reportanto?
                  </Text>
              )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.categoria}
                  </Text>
                )}
              </Descriptions.Item>
            ) : (
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    ¿Trámite o queja?
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.es_tramite ? 'Trámite' : 'Queja'}
                  </Text>
                )}
              </Descriptions.Item>
            )}
            {data?.es_tramite ? (
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    Tipo de protesta
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.motivo}
                  </Text>
                )}
              </Descriptions.Item>
            ) : (
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    Tipo de Visita
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {tiposDeVisitasDomiciliarias
                      .find((e) => e.id === data?.tipo_de_visita_domiciliaria)?.nombre}
                  </Text>
                )}
              </Descriptions.Item>

            )}
            {data?.submotivo && (
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    Motivo
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {data?.submotivo}
                  </Text>
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label={(
                <Text $accent $bold>
                  Fecha de los hechos
                </Text>
              )}
            >
              {loading ? (
                <RowSkeleton />
              ) : (
                <Text>
                  {data?.fecha}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={(
                <Text $accent $bold>
                  Hora aproximada de los hechos
                </Text>
              )}
            >
              {loading ? (
                <RowSkeleton />
              ) : (
                <Text>
                  {data?.hora}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={(
                <Text $accent $bold>
                  Oficina en donde sucedieron los hechos
                </Text>
              )}
            >
              {loading ? (
                <RowSkeleton />
              ) : (
                <Text>
                  {data?.oficina}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={(
                <Text $accent $bold>
                  Tramite/Servicio que quería realizar
                </Text>
              )}
            >
              {loading ? (
                <RowSkeleton />
              ) : (
                <Text>
                  {data?.tramite}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={(
                <Text $accent $bold>
                  Evidencias
                </Text>
              )}
            >
              {loading ? (
                <RowSkeleton />
              ) : (
                <Text>
                  {data?.hay_evidencias}
                </Text>
              )}
            </Descriptions.Item>
            {data?.hay_testigos && (
              <Descriptions.Item
                label={(
                  <Text $accent $bold>
                    Testigos
                  </Text>
                )}
              >
                {loading ? (
                  <RowSkeleton />
                ) : (
                  <Text>
                    {testigos.map((testigo) => testigo.descripcion).join(', ')}
                  </Text>
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label={(
                <Text $accent $bold>
                  Detalle de los hechos
                </Text>
              )}
              span={2}
            >
              {loading ? (
                <RowSkeleton />
              ) : (
                <Text>
                  {data?.detalle}
                </Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </>
      ) : (
        <Title level={4}>
          ¿Estás seguro de que la información es correcta?
        </Title>
      )}
    </Modal>
  );
};

ModalValidateInfo.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  isAnonimo: PropTypes.bool.isRequired,
  testigos: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    descripcion: PropTypes.string,
  })).isRequired,
  callback: PropTypes.func.isRequired,
};

export default ModalValidateInfo;
